import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { addHours, isAfter } from 'date-fns';
import type { ComponentProps } from 'react';
import { FormattedMessage } from 'react-intl';

import {
  SearchSortDirection,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { SEARCH_PARAMS } from '@sorare/core/src/components/search/InstantSearch/types';
import { buildFilterQuery } from '@sorare/core/src/components/search/InstantSearch/utils';
import type { PrintablePlayerSortName } from '@sorare/core/src/contexts/config';
import { ClickMarketHomeSection_Target } from '@sorare/core/src/protos/events/so5/shared/events';

import { useMarketHomeFilters } from 'hooks/useMarketHomeFilters';

import { SlideShow } from '../SlideShow';
import { Prediction } from './Prediction';
import { HeaderSection } from '../HeaderSection';
import { EmptyState } from './EmptyState';
import { PlayerBlockSkeleton } from '../PlayerBlockSkeleton';
import type {
  BestPredictions_searchPlayers,
  BestPredictions_so5Fixture,
} from './__generated__/index.graphql';

const PREDICTIONS_READY = {
  [Sport.FOOTBALL]: -48,
  [Sport.BASEBALL]: null,
  [Sport.NBA]: null,
};

export const PREDICTIONS_SORTS_BY_SPORT = {
  [Sport.FOOTBALL]: [
    {
      field: 'next_classic_fixture_projected_grade' as const,
      direction: SearchSortDirection.ASC,
    },
  ],
  [Sport.BASEBALL]: [
    {
      field: 'next_classic_fixture_projected_score' as const,
      direction: SearchSortDirection.DESC,
    },
  ],
  [Sport.NBA]: [
    {
      field: 'gain_on_classic_projection' as const,
      direction: SearchSortDirection.DESC,
    },
  ],
};

const getPrintablePlayerSortName = (sport: Sport): PrintablePlayerSortName => {
  const [{ field, direction }] = PREDICTIONS_SORTS_BY_SPORT[sport] || [
    {
      field: 'next_classic_fixture_projected_score',
      direction: 'DESC',
    },
  ];
  return `${direction}-${field}`;
};

const SKELETONS = Array.from({ length: 16 }, (_, index) => ({ index }));
const SKELETONS_PAIRS = SKELETONS.reduce<{ index: number }[][]>(
  (acc, _, index) => {
    if (index % 2 === 0) {
      acc.push([SKELETONS[index], SKELETONS[index + 1]]);
    }
    return acc;
  },
  []
);

type AnyPlayer = BestPredictions_searchPlayers['hits'][number]['anyPlayer'];

type Props = {
  predictions?: BestPredictions_searchPlayers;
  loading: boolean;
  sport: Sport;
  onMarketClick: (slug: string) => void;
  onStatsClick: (slug: string) => void;
  track?: ComponentProps<typeof Prediction>['track'];
  so5Fixture?: Nullable<BestPredictions_so5Fixture>;
};
const now = new Date();

export const BestPredictions = ({
  predictions,
  loading,
  onMarketClick,
  onStatsClick,
  track,
  so5Fixture,
  sport,
}: Props) => {
  const { scoutAllUrl } = useMarketHomeFilters();
  const anyPlayers = (
    predictions?.hits?.map(hit => hit.anyPlayer) || []
  ).filter(Boolean);

  const anyPlayersByPair = anyPlayers?.reduce<AnyPlayer[][]>(
    (acc, ap, index) => {
      if (index % 2 === 0) {
        acc.push([ap]);
      } else {
        acc[acc.length - 1].push(ap);
      }
      return acc;
    },
    []
  );

  const { startDate, seasonGameWeek } = so5Fixture || {};

  const predictionsReadyDate =
    !!startDate &&
    !!PREDICTIONS_READY[sport] &&
    addHours(startDate, PREDICTIONS_READY[sport]);

  const canShowPredictions = predictionsReadyDate
    ? isAfter(now, predictionsReadyDate)
    : true;

  if (anyPlayers.length === 0 && !loading) {
    return null;
  }

  return (
    <Vertical gap={3}>
      <HeaderSection
        title={
          <FormattedMessage
            id="MarketHomePage.BestPredictions.title.v4"
            defaultMessage="Top Projected Picks for GW{gameWeek}"
            values={{
              gameWeek: seasonGameWeek,
            }}
          />
        }
        button={{
          label: (
            <FormattedMessage
              id="MarketHomePage.BestPredictions.scoutAll"
              defaultMessage="Scout All"
            />
          ),
          to: `${scoutAllUrl}?${buildFilterQuery({
            [SEARCH_PARAMS.SORT]: getPrintablePlayerSortName(sport),
          })}`,
          onClick: () =>
            track?.({ target: ClickMarketHomeSection_Target.ALL_BUTTON }),
        }}
      />
      {!!canShowPredictions && (
        <SlideShow>
          <Horizontal gap={1.5}>
            {!!loading &&
              SKELETONS_PAIRS.map(pair => (
                <Vertical gap={1.5} key={pair[0].index}>
                  <PlayerBlockSkeleton key={pair[0].index} />
                  <PlayerBlockSkeleton key={pair[1].index} />
                </Vertical>
              ))}
            {!loading &&
              anyPlayersByPair?.map(pair => (
                <Vertical gap={1.5} key={pair?.[0]?.slug}>
                  {!!pair?.[0] && (
                    <Prediction
                      key={pair[0]?.slug}
                      anyPlayer={pair[0]}
                      onStatsClick={onStatsClick}
                      onMarketClick={onMarketClick}
                      track={track}
                    />
                  )}
                  {!!pair?.[1] && (
                    <Prediction
                      key={pair[1]?.slug}
                      anyPlayer={pair[1]}
                      onStatsClick={onStatsClick}
                      onMarketClick={onMarketClick}
                      track={track}
                    />
                  )}
                </Vertical>
              ))}
          </Horizontal>
        </SlideShow>
      )}
      {!canShowPredictions && !!predictionsReadyDate && (
        <EmptyState startDatetime={predictionsReadyDate} />
      )}
    </Vertical>
  );
};

BestPredictions.fragments = {
  searchPlayers: gql`
    fragment BestPredictions_searchPlayers on SearchPlayers {
      hits {
        id
        anyPlayer {
          slug
          displayName
          ...Prediction_anyPlayer
        }
      }
    }
    ${Prediction.fragments.anyPlayer}
  ` as TypedDocumentNode<BestPredictions_searchPlayers>,
  so5Fixture: gql`
    fragment BestPredictions_so5Fixture on So5Fixture {
      slug
      startDate
      seasonGameWeek
    }
  ` as TypedDocumentNode<BestPredictions_so5Fixture>,
};
